import {
  ApplicationRef,
  createComponent,
  EmbeddedViewRef,
  Injectable,
  Optional,
  Provider,
  SkipSelf,
} from '@angular/core';
import { ComponentRef, TemplateRef, ViewContainerRef } from '@angular/core';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';

import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ITcsLoadingConfig } from './loading.service';
import { LoadingStyle, TcsLoadingComponent } from './components/loading/loading.component';
import { TcsLoadingContext } from './loading.directive';

export interface IInternalLoadingOptions extends ITcsLoadingConfig {
  height?: number;
  style?: LoadingStyle;
}

export interface ILoadingRef {
  observable: Observable<any>;
  componentRef?: ComponentRef<any>;
  subject: Subject<any>;
  times: number;
}

@Injectable({
  providedIn: 'root',
})
export class TcsLoadingFactory {
  constructor(private _overlay: Overlay, private _applicationRef: ApplicationRef) {}

  /**
   * Uses material `Overlay` services to create a DOM element and attach the loading component
   * into it. Leveraging the state and configuration from it.
   *
   * Saves a reference in context to be called when registering/resolving the loading element.
   */
  public createFullScreenComponent(options: ITcsLoadingConfig): ILoadingRef {
    (options as IInternalLoadingOptions).height = undefined;
    (options as IInternalLoadingOptions).style = LoadingStyle.FullScreen;
    const loadingRef: ILoadingRef = this._initializeContext();
    let loading = false;
    let overlayRef: OverlayRef;
    loadingRef.observable.pipe(distinctUntilChanged()).subscribe((registered: number) => {
      if (registered > 0 && !loading) {
        loading = true;
        overlayRef = this._createOverlay();
        loadingRef.componentRef = overlayRef.attach(new ComponentPortal(TcsLoadingComponent));
        this._mapOptions(options, loadingRef.componentRef?.instance);
        loadingRef.componentRef?.instance.show();
        loadingRef.componentRef?.changeDetectorRef.detectChanges();
      } else if (registered <= 0 && loading) {
        loading = false;
        loadingRef.componentRef?.instance.hide();
        loadingRef.componentRef?.destroy();
        overlayRef.detach();
        overlayRef.dispose();
      }
    });
    return loadingRef;
  }

  /**
   * Creates a loading component dynamically and attaches it into the given viewContainerRef.
   * Leverages TemplatePortals from material to inject the template inside of it so it fits
   * perfectly when overlaying it.
   *
   * Saves a reference in context to be called when registering/resolving the loading element.
   */
  public createOverlayComponent(
    options: ITcsLoadingConfig,
    viewContainerRef: ViewContainerRef,
    templateRef: TemplateRef<object>,
  ): ILoadingRef {
    (options as IInternalLoadingOptions).height = undefined;
    (options as IInternalLoadingOptions).style = LoadingStyle.Overlay;
    const loadingRef: ILoadingRef = this._createComponent(options);
    let loading = false;

    if (loadingRef.componentRef != null) {
      loadingRef.componentRef.instance.content = new TemplatePortal(templateRef, viewContainerRef);
      viewContainerRef.clear();
      viewContainerRef.insert(loadingRef.componentRef?.hostView, 0);
    }

    loadingRef.observable.pipe(distinctUntilChanged()).subscribe((registered: number) => {
      if (registered > 0 && !loading) {
        loading = true;
        loadingRef.componentRef?.instance.show();
      } else if (registered <= 0 && loading) {
        loading = false;
        loadingRef.componentRef?.instance.hide();
      }
    });
    return loadingRef;
  }

  /**
   * Creates a loading component dynamically and attaches it into the given viewContainerRef.
   * Replaces the template with the loading component depending if it was registered or resolved.
   *
   * Saves a reference in context to be called when registering/resolving the loading element.
   */
  public createReplaceComponent(
    options: ITcsLoadingConfig,
    viewContainerRef: ViewContainerRef,
    templateRef: TemplateRef<object>,
    context: TcsLoadingContext,
  ): ILoadingRef {
    (options as IInternalLoadingOptions).height = undefined;
    (options as IInternalLoadingOptions).style = LoadingStyle.None;
    const loadingRef: ILoadingRef = this._createComponent(options);
    let loading = false;
    // passing context so when the template is attached, we can keep the reference of the variables
    const contentRef: EmbeddedViewRef<object> = viewContainerRef.createEmbeddedView(
      templateRef,
      context,
    );
    loadingRef.observable.pipe(distinctUntilChanged()).subscribe((registered: number) => {
      if (registered > 0 && !loading && loadingRef.componentRef != null) {
        loading = true;
        // detach the content and attach the loader if loader is there
        const index: number = viewContainerRef.indexOf(loadingRef.componentRef.hostView);
        if (index < 0) {
          viewContainerRef.detach(viewContainerRef.indexOf(contentRef));
          viewContainerRef.insert(loadingRef.componentRef.hostView, 0);
        }
        loadingRef.componentRef?.instance.show();
      } else if (registered <= 0 && loading && loadingRef.componentRef != null) {
        loading = false;
        loadingRef.componentRef?.instance.hide();
        // detach loader and attach the content if content is there
        const index: number = viewContainerRef.indexOf(contentRef);
        if (index < 0) {
          viewContainerRef.detach(viewContainerRef.indexOf(loadingRef.componentRef.hostView));
          viewContainerRef.insert(contentRef, 0);
        }
        /**
         * Need to call "markForCheck" and "detectChanges" on attached template, so its detected by parent component when attached
         * with "OnPush" change detection
         */
        contentRef.detectChanges();
        contentRef.markForCheck();
      }
    });
    return loadingRef;
  }

  /**
   * Creates a fullscreen overlay for the loading usage.
   */
  private _createOverlay(): OverlayRef {
    const state: OverlayConfig = new OverlayConfig();
    state.hasBackdrop = false;
    state.positionStrategy = this._overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();
    return this._overlay.create(state);
  }

  /**
   * Creates a generic component dynamically waiting to be attached to a viewContainerRef.
   */
  private _createComponent(options: IInternalLoadingOptions): ILoadingRef {
    const compRef: ILoadingRef = this._initializeContext();
    const environmentInjector = this._applicationRef.injector;
    compRef.componentRef = createComponent(TcsLoadingComponent, {
      environmentInjector,
    });
    this._mapOptions(options, compRef.componentRef.instance);
    return compRef;
  }

  /**
   * Initialize context for loading component.
   */
  private _initializeContext(): ILoadingRef {
    const subject: Subject<any> = new Subject<any>();
    return {
      observable: subject.asObservable(),
      subject,
      componentRef: undefined,
      times: 0,
    };
  }

  /**
   * Maps configuration to the loading component instance.
   */
  private _mapOptions(options: IInternalLoadingOptions, instance: TcsLoadingComponent): void {
    if (options.style != null) {
      instance.style = options.style;
    }

    if (options.type !== undefined) {
      instance.type = options.type;
    }
    if (options.height !== undefined) {
      instance.height = options.height;
    }
    if (options.mode !== undefined) {
      instance.mode = options.mode;
    }
    if (options.color !== undefined) {
      instance.color = options.color;
    }
  }
}

export function loadingFactoryProviderFactory(
  parent: TcsLoadingFactory,
  overlay: Overlay,
  applicationRef: ApplicationRef,
): TcsLoadingFactory {
  return parent ?? new TcsLoadingFactory(overlay, applicationRef);
}

export const loadingFactoryProvider: Provider = {
  // If there is already a service available, use that. Otherwise, provide a new one.
  provide: TcsLoadingFactory,
  deps: [[new Optional(), new SkipSelf(), TcsLoadingFactory], Overlay, ApplicationRef],
  useFactory: loadingFactoryProviderFactory,
};
